import React, { ReactElement } from 'react';
import {
    useInfiniteQuery,
    UseInfiniteQueryResult,
    useQuery,
} from '@tanstack/react-query';
import { PageStatusMessage } from '@pb/shared/ui/organisms/page-status-message';
import { LoadingSpinner } from '@pb/shared/ui/molecules/loading-spinner';
import { ResponsiveRender } from '@pb/shared/ui/atoms/responsive-render';
import { Heading } from '@pb/shared/ui/atoms/heading';
import { getEnvironmentSettings, TEnvironmentSettings } from '@pb/services';
import { useIsOnline } from '@pb/hooks';

import { ThreadsView } from '../../organisms/threads-view';
import { SelectionInfo } from '../../organisms/SelectionInfo/selection-info';
import { Layout } from '../../organisms/Layout';
import { ConnectionIssues } from '../../organisms/ConnectionIssues';
import { NoMessagesNotification } from '../../atoms/no-messages-notification';
import { getUser } from '../../../services/user.service';
import { getThreads } from '../../../services/threads.service';
import { EnvironmentContext } from '../../../context';
import { TThreadsResponse } from '../../../configuration/common.types';
import { title } from '../../../configuration/common.text';

import {
    StyledContainer,
    StyledHeader,
    StyledNav,
    StyledThreadWrapper,
    StyledWrapper,
} from './index-page.styled';

export function Threads(): ReactElement {
    const isOnline = useIsOnline();

    const user = useQuery(['user'], getUser, { refetchOnWindowFocus: false });

    const userDiscriminator = user?.data?.userDiscriminator;
    const isCustomer = userDiscriminator === 'Customer';
    const threads: UseInfiniteQueryResult<TThreadsResponse, unknown> =
        useInfiniteQuery(
            ['threads'],
            pageParameter =>
                getThreads({
                    ...pageParameter.pageParam,
                    noFilter: isCustomer,
                }),
            {
                getNextPageParam: lastPage => {
                    if (!lastPage?.pageInfo?.hasNextPage) return undefined;
                    return {
                        cursor: lastPage.threads[lastPage.threads.length - 1]
                            .cursor,
                        limit: 50,
                    };
                },
                refetchInterval: (_, query) => {
                    return query.state.status === 'error' ? false : 30000;
                },
                refetchOnWindowFocus: query => {
                    return query.state.status !== 'error';
                },
                enabled: !!user.data,
            }
        );

    if (user.isError) {
        return (
            <Layout pageTitle={`${title} - Please log in`}>
                <PageStatusMessage pageStatusType={'notAuthorised'} />
            </Layout>
        );
    }
    if (user.isLoading || threads.isLoading) {
        return (
            <Layout pageTitle={`${title} - loading`}>
                <LoadingSpinner />
            </Layout>
        );
    }

    if (threads.isError) {
        return (
            <Layout pageTitle={`${title} - there has been an error`}>
                <PageStatusMessage>
                    <Heading level={1} marginBottom="regular">
                        Sorry, there has been an error.
                    </Heading>
                </PageStatusMessage>
            </Layout>
        );
    }

    return (
        <Layout pageTitle={title} isCustomer={isCustomer}>
            <StyledContainer fullWidth>
                <StyledHeader>
                    <Heading level={1} marginBottom="none" size="small">
                        Your messages
                    </Heading>
                </StyledHeader>
                {!isOnline && <ConnectionIssues />}
                <StyledWrapper>
                    <StyledNav>
                        {threads.data?.pages.length === 0 && (
                            <ResponsiveRender showOnBreakpoints={['xs', 's']}>
                                <NoMessagesNotification />
                            </ResponsiveRender>
                        )}
                        <ThreadsView threads={threads} user={user} />
                    </StyledNav>
                    <StyledThreadWrapper>
                        {threads.data.pages.length > 0 && (
                            <SelectionInfo
                                noThreads={threads.data.pages.length === 0}
                            />
                        )}
                    </StyledThreadWrapper>
                </StyledWrapper>
            </StyledContainer>
        </Layout>
    );
}

/* istanbul ignore next */
export function IndexPage() {
    return (
        <EnvironmentContext.Provider
            value={getEnvironmentSettings() as TEnvironmentSettings}
        >
            <Threads />
        </EnvironmentContext.Provider>
    );
}
